import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
   Box,
   Typography,
   CircularProgress,
   List,
   ListItem,
   ListItemText,
   Paper,
   Divider,
   Chip,
   IconButton,
} from '@mui/material';
import { Message as MessageIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useUser } from '../contexts/UserContext';
import { getAuthHeaders } from '../utils/auth';

interface Inquiry {
   id: string;
   senderUserId: string | null;
   senderEmail: string;
   senderName: string | null;
   albumId: string;
   albumTitle: string | null;
   message: string;
   createdAt: string | null;
}

const Inbox: React.FC = () => {
   const { currentUser } = useUser();
   const [inquiries, setInquiries] = useState<Inquiry[]>([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState<string | null>(null);

   useEffect(() => {
      if (!currentUser?.uid) return;
      let cancelled = false;
      setLoading(true);
      setError(null);
      (async () => {
         try {
            const headers = await getAuthHeaders();
            const res = await fetch(
               `${process.env.REACT_APP_API_URL}/api/profile/photographer-inquiries`,
               { headers }
            );
            if (cancelled) return;
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
               setError(data.message || data.error || 'Failed to load messages');
               setInquiries([]);
               return;
            }
            setInquiries(data.inquiries || []);
         } catch (err: any) {
            if (!cancelled) {
               setError(err?.message || 'Failed to load messages');
               setInquiries([]);
            }
         } finally {
            if (!cancelled) setLoading(false);
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [currentUser?.uid]);

   const formatDate = (iso: string | null) => {
      if (!iso) return '';
      try {
         const d = new Date(iso);
         return d.toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric',
            year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
            hour: '2-digit',
            minute: '2-digit',
         });
      } catch {
         return iso;
      }
   };

   return (
      <Box sx={{ maxWidth: 720, mx: 'auto', py: 3, px: 2 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <IconButton component={Link} to="/dashboard" size="small" aria-label="Back to dashboard">
               <BackIcon />
            </IconButton>
            <MessageIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h5" fontWeight={600}>
               Inbox
            </Typography>
         </Box>
         <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Messages from people who contacted you from your album photos.
         </Typography>

         {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
               <CircularProgress />
            </Box>
         ) : error ? (
            <Typography color="error">{error}</Typography>
         ) : inquiries.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
               <MessageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
               <Typography color="text.secondary">No messages yet.</Typography>
               <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  When someone sends you a message from your photographer profile on an album, it will appear here.
               </Typography>
            </Paper>
         ) : (
            <Paper variant="outlined">
               <List disablePadding>
                  {inquiries.map((inq, idx) => (
                     <React.Fragment key={inq.id}>
                        {idx > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                           <ListItemText
                              primary={
                                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                       {inq.senderName || inq.senderEmail || 'Someone'}
                                    </Typography>
                                    {inq.senderName && (
                                       <Typography variant="body2" color="text.secondary">
                                          ({inq.senderEmail})
                                       </Typography>
                                    )}
                                    {inq.albumTitle && (
                                       <Chip label={inq.albumTitle} size="small" variant="outlined" />
                                    )}
                                 </Box>
                              }
                              secondary={
                                 <>
                                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                       {inq.message}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                       {formatDate(inq.createdAt)}
                                       {inq.albumId && (
                                        <>
                                           {' · '}
                                           <Link
                                              to={`/albums/public/${inq.albumId}`}
                                              style={{ color: 'inherit' }}
                                           >
                                              View album
                                           </Link>
                                        </>
                                     )}
                                    </Typography>
                                 </>
                              }
                           />
                        </ListItem>
                     </React.Fragment>
                  ))}
               </List>
            </Paper>
         )}
      </Box>
   );
};

export default Inbox;
